import React, { useState } from 'react';
import { X } from 'lucide-react';

/**
 * 八字发布弹窗组件
 * @param {boolean} isOpen - 是否打开弹窗
 * @param {function} onClose - 关闭弹窗回调
 * @param {function} onSubmit - 提交回调
 * @param {object} chartData - 卦例基本信息
 */
const BaziPublish = ({ isOpen, onClose, onSubmit, chartData }) => {
  const [publishType, setPublishType] = useState('qiuzhu');
  const [qiuzhuForm, setQiuzhuForm] = useState({
    isPaid: false,
    paymentAmount: '',
    endDate: '',
  });
  const [shareForm, setShareForm] = useState({
    isCharged: false,
    chargeAmount: '',
    freeContentRatio: 100,
  });
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = {
        chartId: chartData?.id,
        publishType,
        ...(publishType === 'qiuzhu' ? {
          isPaid: qiuzhuForm.isPaid,
          paymentAmount: qiuzhuForm.isPaid ? parseFloat(qiuzhuForm.paymentAmount) || 0 : 0,
          endDate: qiuzhuForm.endDate || null,
        } : {
          isCharged: shareForm.isCharged,
          chargeAmount: shareForm.isCharged ? (parseFloat(shareForm.chargeAmount) || 0) : 0,
          freeContentRatio: Math.max(0, Math.min(100, parseInt(shareForm.freeContentRatio) || 100)),
        }),
      };
      
      console.log('BaziPublish formData:', formData);
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('发布失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">发布八字命盘</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* 发布类型选择 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">发布类型</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="publishType"
                  value="qiuzhu"
                  checked={publishType === 'qiuzhu'}
                  onChange={(e) => setPublishType(e.target.value)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">求助 - 发布求助信息，等待命理师解答</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="publishType"
                  value="share"
                  checked={publishType === 'share'}
                  onChange={(e) => setPublishType(e.target.value)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">分享 - 分享已完成的命盘分析</span>
              </label>
            </div>
          </div>

          {/* 求助表单 */}
          {publishType === 'qiuzhu' && (
            <div className="space-y-4 p-3 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900">求助设置</h4>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={qiuzhuForm.isPaid}
                    onChange={(e) => setQiuzhuForm(prev => ({ ...prev, isPaid: e.target.checked }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">设置悬赏积分</span>
                </label>
              </div>

              {qiuzhuForm.isPaid && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">悬赏积分 (分)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={qiuzhuForm.paymentAmount}
                    onChange={(e) => setQiuzhuForm(prev => ({ ...prev, paymentAmount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="请输入悬赏积分"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">截止日期 (可选)</label>
                <input
                  type="date"
                  value={qiuzhuForm.endDate}
                  onChange={(e) => setQiuzhuForm(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          )}

          {/* 分享表单 */}
          {publishType === 'share' && (
            <div className="space-y-4 p-3 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900">分享设置</h4>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={shareForm.isCharged}
                    onChange={(e) => setShareForm(prev => ({ ...prev, isCharged: e.target.checked }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">设置收费积分</span>
                </label>
              </div>

              {shareForm.isCharged && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">收费积分 (分)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={shareForm.chargeAmount}
                      onChange={(e) => setShareForm(prev => ({ ...prev, chargeAmount: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="请输入收费积分"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      免费浏览内容占比：{shareForm.freeContentRatio}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="10"
                      value={shareForm.freeContentRatio}
                      onChange={(e) => setShareForm(prev => ({ ...prev, freeContentRatio: e.target.value }))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>全部付费</span>
                      <span>全部免费</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 提交按钮 */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? '发布中...' : '发布'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BaziPublish;